// Editorial module (PED) contracts.
import { z } from 'zod';

const Uuid = z.string().uuid();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida');
const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida');
const Color = z.enum(['indigo', 'blue', 'teal', 'green', 'lime', 'amber', 'orange', 'red', 'pink', 'purple']);
const HttpsUrl = z.string().trim().max(2000).regex(/^https:\/\/\S+$/, 'Inserisci un link che inizia con https://');

export const PLATFORMS = ['instagram', 'tiktok', 'youtube', 'facebook', 'threads', 'linkedin', 'x', 'website', 'newsletter', 'podcast', 'other'] as const;
export type Platform = (typeof PLATFORMS)[number];
export const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', facebook: 'Facebook', threads: 'Threads',
  linkedin: 'LinkedIn', x: 'X', website: 'Sito', newsletter: 'Newsletter', podcast: 'Podcast', other: 'Altro',
};
/** Caption length limits used for the live counter (characters). */
export const PLATFORM_CAPTION_LIMITS: Partial<Record<Platform, number>> = {
  instagram: 2200, tiktok: 2200, facebook: 63206, threads: 500, linkedin: 3000, x: 280, youtube: 5000,
};

export const CONTENT_ROLES = ['owner', 'copywriter', 'designer', 'video_editor', 'talent', 'reviewer'] as const;
export type ContentRole = (typeof CONTENT_ROLES)[number];
export const CONTENT_ROLE_LABELS: Record<ContentRole, string> = {
  owner: 'Responsabile', copywriter: 'Copy', designer: 'Grafica', video_editor: 'Video editor', talent: 'Talent', reviewer: 'Revisore',
};

export const FIELD_TYPES = ['text', 'number', 'date', 'select', 'multi_select', 'url', 'checkbox', 'user'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];
export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Testo', number: 'Numero', date: 'Data', select: 'Selezione', multi_select: 'Selezione multipla',
  url: 'Link', checkbox: 'Casella', user: 'Persona',
};

export const DEADLINE_RULES = ['monday_before_last_week', 'last_day_prev_month', 'none'] as const;
export type DeadlineRule = (typeof DEADLINE_RULES)[number];
export const DEADLINE_RULE_LABELS: Record<DeadlineRule, string> = {
  monday_before_last_week: 'Il lunedì prima dell’ultima settimana del mese precedente',
  last_day_prev_month: 'L’ultimo giorno del mese precedente',
  none: 'Nessuna scadenza',
};

export const DEFAULT_FORMATS = ['Reel', 'Carosello', 'Post', 'Storia', 'TikTok', 'Short', 'Video YouTube', 'Live', 'Articolo'];

export const CONTENT_STATE_CATEGORIES = ['backlog', 'todo', 'in_progress', 'review', 'scheduled', 'done', 'canceled'] as const;
export const STATE_CATEGORY_LABELS: Record<(typeof CONTENT_STATE_CATEGORIES)[number], string> = {
  backlog: 'Idea', todo: 'Da fare', in_progress: 'In lavorazione', review: 'In revisione', scheduled: 'Programmato', done: 'Pubblicato', canceled: 'Archiviato',
};

// ------------------------------------------------------------------ settings inputs
export const UpdateEditorialSettingsInput = z.object({
  formats: z.array(z.string().trim().min(1).max(40)).max(40).optional(),
  deadlineRule: z.enum(DEADLINE_RULES).optional(),
  cardFields: z.array(z.enum(['series', 'format', 'channels', 'owner', 'state'])).max(5).optional(),
});

export const EditorialStateInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(40),
  category: z.enum(CONTENT_STATE_CATEGORIES),
  requiresApproval: z.boolean().default(false),
  color: Color.optional(),
});

export const ReorderInput = z.object({ ids: z.array(Uuid).min(1).max(100) });

export const ChannelInput = z.object({
  platform: z.enum(PLATFORMS),
  handle: z.string().trim().min(1, 'Indica l’account, es. @ragazze').max(80),
});

export const SeriesInput = z.object({
  name: z.string().trim().min(1, 'Scrivi il nome della rubrica').max(80),
  description: z.string().max(2000).nullable().optional(),
  color: Color.default('indigo'),
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
  defaultTime: TimeOfDay.nullable().default(null),
  defaultFormat: z.string().trim().max(40).nullable().default(null),
  defaultChannelIds: z.array(Uuid).max(20).default([]),
  ownerUserId: Uuid.nullable().default(null),
});
export type SeriesInput = z.input<typeof SeriesInput>;

export const CustomFieldInput = z.object({
  label: z.string().trim().min(1, 'Scrivi il nome del campo').max(60),
  fieldType: z.enum(FIELD_TYPES),
  options: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
});

// ------------------------------------------------------------------ content inputs
export const ContentMemberInput = z.object({ userId: Uuid, role: z.enum(CONTENT_ROLES) });

const contentFields = {
  title: z.string().trim().min(1, 'Scrivi un titolo').max(300),
  plannedDate: DateOnly.nullable(),
  plannedTime: TimeOfDay.nullable(),
  seriesId: Uuid.nullable(),
  format: z.string().trim().max(40).nullable(),
  stateId: Uuid,
  caption: z.string().max(10000),
  brief: z.string().max(20000),
  designUrl: HttpsUrl.nullable().or(z.literal('').transform(() => null)),
  assetUrl: HttpsUrl.nullable().or(z.literal('').transform(() => null)),
  channelIds: z.array(Uuid).max(20),
  members: z.array(ContentMemberInput).max(30),
  customFields: z.record(z.string().max(60), z.unknown()),
};

export const CreateContentInput = z.object({ workspaceId: Uuid, ...contentFields }).partial({
  plannedDate: true, plannedTime: true, seriesId: true, format: true, stateId: true, caption: true, brief: true,
  designUrl: true, assetUrl: true, channelIds: true, members: true, customFields: true,
});
export type CreateContentInput = z.input<typeof CreateContentInput>;

export const UpdateContentInput = z.object(contentFields).partial();
export type UpdateContentInput = z.input<typeof UpdateContentInput>;

export const PublicationInput = z.object({
  publishedUrl: HttpsUrl.nullable().or(z.literal('').transform(() => null)),
});

export const ContentListQuery = z.object({
  workspaceId: Uuid.optional(),
  from: DateOnly,
  to: DateOnly,
  seriesId: Uuid.optional(),
  includeUnscheduled: z.enum(['0', '1']).optional(),
});
export type ContentListQuery = z.input<typeof ContentListQuery>;

export const GenerateMonthInput = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mese non valido') });
export const RequestChangesInput = z.object({ comment: z.string().trim().min(1, 'Spiega cosa va cambiato').max(5000) });

// ------------------------------------------------------------------ responses
export interface EditorialState {
  id: string;
  name: string;
  category: (typeof CONTENT_STATE_CATEGORIES)[number];
  requiresApproval: boolean;
  color: string | null;
  position: number;
  itemCount: number;
}

export interface EditorialChannel {
  id: string;
  platform: Platform;
  handle: string;
}

export interface EditorialSeries {
  id: string;
  name: string;
  description: string | null;
  color: z.infer<typeof Color>;
  weekdays: number[];
  defaultTime: string | null;
  defaultFormat: string | null;
  defaultChannelIds: string[];
  ownerUserId: string | null;
  position: number;
}

export interface CustomField {
  id: string;
  key: string;
  label: string;
  fieldType: FieldType;
  options: string[];
}

export interface EditorialConfig {
  workspaceId: string;
  enabled: boolean;
  formats: string[];
  deadlineRule: DeadlineRule;
  cardFields: string[];
  states: EditorialState[];
  channels: EditorialChannel[];
  series: EditorialSeries[];
  fields: CustomField[];
  permissions: { view: boolean; create: boolean; edit: boolean; approve: boolean; admin: boolean };
}

export interface ContentItem {
  id: string;
  workspaceId: string;
  workspaceName: string;
  workspaceColor: string;
  title: string;
  plannedDate: string | null;
  plannedTime: string | null;
  state: { id: string; name: string; category: EditorialState['category']; requiresApproval: boolean; color: string | null };
  series: { id: string; name: string; color: string } | null;
  format: string | null;
  caption: string;
  brief: string;
  designUrl: string | null;
  assetUrl: string | null;
  channels: { id: string; platform: Platform; handle: string; publishedUrl: string | null; status: string }[];
  members: { userId: string; displayName: string; role: ContentRole }[];
  approval: { status: 'not_required' | 'pending' | 'approved' | 'changes_requested'; by: { id: string; displayName: string } | null; at: string | null };
  customFields: Record<string, unknown>;
  createdBy: { id: string; displayName: string };
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  permissions: { edit: boolean; delete: boolean; approve: boolean; comment: boolean };
}

export interface GenerateMonthResult {
  created: number;
  skipped: number;
}

/** Deadline for completing the plan of `month` (YYYY-MM) under a rule; null when there is no deadline. */
export function pedDeadline(month: string, rule: DeadlineRule): string | null {
  if (rule === 'none') return null;
  const [y, m] = month.split('-').map(Number) as [number, number];
  const firstOfMonth = Date.UTC(y, m - 1, 1);
  const lastPrev = new Date(firstOfMonth - 86_400_000); // last day of the previous month
  if (rule === 'last_day_prev_month') return lastPrev.toISOString().slice(0, 10);
  // "Il lunedì precedente all'ultima settimana del mese": the last Monday of the previous month
  // starts its last week; the deadline is the Monday before that.
  const d = new Date(lastPrev);
  while (d.getUTCDay() !== 1) d.setUTCDate(d.getUTCDate() - 1);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------ YouTube publishing from a content item
export const YOUTUBE_PRIVACY = ['public', 'unlisted', 'private'] as const;
export type YoutubePrivacy = (typeof YOUTUBE_PRIVACY)[number];
export const YoutubePublishQuery = z.object({ privacy: z.enum(YOUTUBE_PRIVACY).default('public') });

export interface YoutubePublishResult {
  item: ContentItem;
  videoUrl: string;
  /** Set when the content's planned date is in the future: YouTube publishes the video at that time. */
  scheduledFor: string | null;
  /** What YouTube applied; differs from the request when Google locks uploads of an unaudited project to private. */
  privacy: string;
  requestedPrivacy: YoutubePrivacy;
}

// ------------------------------------------------------------------ TikTok publishing from a content item
/** What TikTok allows this creator right now (asked before every post, as TikTok's guidelines require). */
export interface TiktokCreatorOptions {
  nickname: string;
  username: string | null;
  privacyOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxDurationSec: number | null;
}

const Flag = z.enum(['0', '1']).default('0').transform((v) => v === '1');
export const TiktokPublishQuery = z.object({
  privacy: z.string().min(1, 'Scegli chi può vedere il video').max(40),
  allowComment: Flag,
  allowDuet: Flag,
  allowStitch: Flag,
  brandOrganic: Flag,
  brandedContent: Flag,
});
export type TiktokPublishQuery = z.input<typeof TiktokPublishQuery>;

export interface TiktokPublishResult {
  item: ContentItem;
  /** TikTok processes the video after the upload: false while it isn't on the profile yet. */
  complete: boolean;
  privacy: string;
}
